import React from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import MessagesPage from '../components/Succeedo/Messages/MessagesPage';

const MESSAGES_QUERY = gql`
  query MyConversations {
    currentUser {
      id
      name
      succeedo_persona
      action_plan {
        open_count(source: "succeedo")
        completed_count(source: "succeedo")
      }
    }
    my_conversations
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($cid: ID!, $body: String!) {
    send_message(conversation_id: $cid, body: $body) {
      message
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0] : '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function PartnerMessagesLoader() {
  const { data, loading, refetch } = useQuery(MESSAGES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [sendReply, { loading: sending }] = useMutation(SEND_MESSAGE);

  const handleSendReply = async ({ conversationId, body }) => {
    if (!conversationId || !body?.trim()) return { ok: false, error: 'Empty message' };
    try {
      const { data: result } = await sendReply({
        variables: { cid: conversationId, body: body.trim() },
      });
      const errors = result?.send_message?.errors || [];
      if (errors.length) return { ok: false, error: errors.join(', ') };
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_message_sent', {
          conversation_id: conversationId,
          is_partner: true,
        });
      }
      refetch();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || 'Could not send. Please try again.' };
    }
  };

  const user = data?.currentUser;
  const persona = (user?.succeedo_persona || 'seller').toUpperCase();
  const userName = user?.name || '';
  const userInitials = deriveInitials(userName);

  const actionPlan = user?.action_plan || {};
  const openCount = actionPlan.open_count || 0;
  const completedCount = actionPlan.completed_count || 0;
  const total = Math.max(openCount + completedCount, 5);
  const planProgress = {
    completed: completedCount,
    total,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
  };

  return (
    <MessagesPage
      conversations={data?.my_conversations || []}
      loading={loading}
      currentUserId={user?.id}
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={openCount}
      isPartner
      breadcrumb="PARTNER MESSAGES"
      onSendReply={handleSendReply}
      onIncomingMessage={refetch}
      sending={sending}
    />
  );
}

const SucceedoPartnerMessagesContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <PartnerMessagesLoader />
  </ApolloProvider>
);

export default SucceedoPartnerMessagesContainer;
